import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { CircularProgress, Link, Typography } from "@mui/material";
import { GoogleMapsProvider } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useAlertsInfiniteQuery } from "@/api/loadAlerts";
import { useHorizontalScroll } from "@/common/HorizontalScroll";
import LoadingModal from "@/common/LoadingModal";
import { useDrawer } from "@/common/SlideoutDrawer/DrawerContext";
import SlideOutDrawer from "@/common/SlideoutDrawer/SlideoutDrawer";
import { isSmallScreen, mailTo } from "@/common/utils";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import {
  BulletinResourceResponse,
  ICreator,
  IUser,
  IUserPermission,
} from "@/types";

import { DisplayAlertDrawer } from "./add/DisplayAlert/DisplayAlertDrawer";
import AlertMap from "./AlertsMap";
import AlertTable from "./AlertTable";
import BulletinCard from "./BulletinCard";
import { ALERT_STATUS } from "./constants";
import { AlertViewContext } from "./context";
import AlertFilterButton from "./Search/AlertFilterButton";
import AlertFilters, {
  alertFilterConfig,
  RenderAlertFilterChips,
} from "./Search/AlertFilters";
import AlertSearchInput from "./Search/AlertSearchInput";
import AlertViewTabs from "./Search/AlertViewTabs";
import { getLocalStorageWithExpiry } from "./utils";
import "./alerts.scss";

interface IAlertListProps {
  isDraftsList?: boolean;
}

export const AlertList = ({ isDraftsList = false }: IAlertListProps) => {
  const { currentUser: user } = useContext(AuthStateContext);
  const {
    totalAlerts,
    setTotalAlerts,
    currentItems,
    setCurrentItems,
    updatedItems,
    setUpdatedItems,
    policeServices,
    filtersVisible,
    setFiltersVisible,
    orgConfig,
    scrollToBulletinId,
    setScrollToBulletinId,
  } = useContext(AlertViewContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const view = searchParams.get("view") || "card";
  const bulletinId = searchParams.get("bulletinId");
  const pinToFront = searchParams.get("pinToFront");
  const compareBulletinId = searchParams.get("compareBulletinId");
  const myAlerts = searchParams.get("myAlerts") === "true";
  const incidentStartDate = searchParams.get("incidentStartDate");
  const incidentEndDate = searchParams.get("incidentEndDate");
  const issuedStartDate = searchParams.get("issuedStartDate");
  const issuedEndDate = searchParams.get("issuedEndDate");
  const rawBulletinType = searchParams.getAll("bulletinType");
  const bulletinType =
    rawBulletinType.length > 0 ? [...new Set(rawBulletinType)].sort() : null;
  const rawAssociatedCrime = searchParams.getAll("associatedCrime");
  const associatedCrime =
    rawAssociatedCrime.length > 0 ? rawAssociatedCrime : null;
  const policeService = searchParams.get("policeService");
  const searchText = searchParams.get("searchText");
  const similarTo = searchParams.get("similarTo");
  const bounds = searchParams.get("bounds");
  const creator_org_id = searchParams.get("creator_org_id");
  const my_agency_bulletins = searchParams.get("my_agency_bulletins");
  const sort_by_incident_time = searchParams.get("sort_by_incident_time");
  const sort_by = searchParams.get("sort_by");
  const sort_order = searchParams.get("sort_order");
  const resolution_status = searchParams.get("resolution_status");
  const engagement = searchParams.get("engagement");
  const group_ids = searchParams
    .getAll("group_id")
    .map((id) => parseInt(id))
    .filter((id) => !isNaN(id));

  const setSearchText = (value) => {
    if (value === "") {
      setSearchParams((searchParams) => {
        searchParams.delete("searchText");
        return searchParams;
      });
      return;
    }
    setSearchParams((searchParams) => {
      searchParams.set("searchText", value);
      return searchParams;
    });
  };

  const { currentUser, setCurrentUser } = useContext(AuthStateContext);
  const scrollRef = useHorizontalScroll();
  const { openDrawer, closeDrawer, setDrawerWidth } = useDrawer();
  const { PUBLISHED } = ALERT_STATUS;

  const topPageRef = useRef<HTMLDivElement | null>(null);
  const bottomPageRef = useRef<HTMLDivElement>(null);
  const searchBarRef = useRef<HTMLInputElement | null>(null);

  const [alertPermissions] = useState(
    currentUser &&
      parseUserPermissions(currentUser?.permissions as IUserPermission[]),
  );
  const readOnly = alertPermissions
    ? !alertPermissions.canCreateBulletin
    : false;

  useEffect(() => {
    // we need to set the default filters for some agencies.
    // if there's no settings in localstorage, we need to get the default filters from the api.
    if (!(currentUser && currentUser.rubi_org_id)) {
      return;
    }

    const applyAgencySettings = () => {
      let agencySettings;
      const cachedSettings = getLocalStorageWithExpiry("agencySettings");

      if (orgConfig && orgConfig.default_filters) {
        agencySettings = orgConfig;
      } else if (cachedSettings) {
        agencySettings = cachedSettings;
      } else {
        return;
      }

      // Check if user has manually unselected the agency filter
      const agencyBulletinChoice = getLocalStorageWithExpiry(
        "myAgencyBulletinsChoice",
      );
      const shouldApplyDefaultFilter =
        !agencyBulletinChoice || agencyBulletinChoice !== "deselected";

      // Apply default filters if:
      // 1. No existing search params in URL
      // 2. User hasn't manually unselected the filter
      // 3. We have valid agency settings with default filters
      if (
        searchParams.toString() === "" &&
        shouldApplyDefaultFilter &&
        agencySettings?.default_filters &&
        Object.keys(agencySettings.default_filters).length > 0
      ) {
        setSearchParams((searchParams) => {
          // Apply all default filters from agency settings
          Object.entries(agencySettings.default_filters).forEach(
            ([key, value]) => {
              if (key === "myAgencyBulletins") {
                searchParams.set("my_agency_bulletins", "true");
              } else if (value !== null && value !== undefined) {
                searchParams.set(key, value.toString());
              }
            },
          );
          return searchParams;
        });
      }
    };

    applyAgencySettings();
  }, [currentUser?.rubi_org_id, orgConfig]);

  const bulletinCardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const setBulletinCardRef = useCallback(
    (id: number) => (node: HTMLDivElement | null) => {
      if (node) {
        bulletinCardRefs.current.set(id, node);
      } else {
        bulletinCardRefs.current.delete(id);
      }
    },
    [],
  );

  // Brings the bulletin the user just came back from into view. The cards are
  // only there once the list has items, so the target stays pending until then
  // instead of being dropped on the first render.
  useEffect(() => {
    if (scrollToBulletinId == null || currentItems.length === 0) return;
    const node = bulletinCardRefs.current.get(scrollToBulletinId);
    node?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    setScrollToBulletinId(null);
  }, [scrollToBulletinId, currentItems]);

  const {
    data,
    error: alertsError,
    fetchNextPage,
    hasNextPage,
    refetch,
    isLoading,
  } = useAlertsInfiniteQuery(
    view === "map" ? null : 25,
    associatedCrime,
    bulletinType,
    policeServices.find((service) => service.name === policeService) || null,
    incidentStartDate,
    incidentEndDate,
    issuedStartDate,
    issuedEndDate,
    isDraftsList ? null : searchText,
    isDraftsList ? isDraftsList && !readOnly : null,
    myAlerts ? (currentUser?.id as string) : null,
    similarTo ? parseInt(similarTo) : null,
    bounds ? bounds : null,
    creator_org_id ? creator_org_id : null,
    my_agency_bulletins ? my_agency_bulletins === "true" : null,
    false, // is_flag
    sort_by_incident_time ? sort_by_incident_time === "true" : null,
    group_ids.length > 0 ? group_ids : null,
    sort_by,
    sort_order,
    resolution_status,
    engagement,
  );

  const [isLoadingAlerts, setIsLoadingAlerts] = useState(isLoading);
  const [isReadOnlyDialogOpen, setIsReadOnlyDialogOpen] = useState(false);
  const [isLoadingModalOpen, setIsLoadingModalOpen] = useState(false);
  const [slideoutWidth] = useState(
    pinToFront && compareBulletinId && compareBulletinId !== bulletinId
      ? window.innerWidth * 0.7
      : 240,
  );
  const bottomRef = useRef(null);

  const loadMore = () => {
    if (hasNextPage && currentItems.length < totalAlerts) {
      fetchNextPage();
    }
  };

  // Navigating away from a bulletin only the current user can see is guarded
  // centrally by accessGuard in AlertViewProvider, so nothing here needs to ask.
  const alertClickHandler = (item, index) => {
    const selectedBulletinId = item?.id?.toString();

    if (!isDraftsList && !isSmallScreen()) {
      let drawerWidth;
      // if pinToFront is true, set compareBulletinId to item.id
      // if pinToFront is false, set bulletinId to item.id
      setSearchParams((searchParams) => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (pinToFront) {
          if (selectedBulletinId === newSearchParams.get("bulletinId")) {
            newSearchParams.delete("compareBulletinId");
            drawerWidth = window.innerWidth * 0.5;
          } else {
            newSearchParams.set("compareBulletinId", selectedBulletinId);
            drawerWidth = window.innerWidth * 0.7;
          }
        } else {
          newSearchParams.set("bulletinId", selectedBulletinId);
          newSearchParams.delete("access");
          drawerWidth = window.innerWidth * 0.5;
        }
        return newSearchParams;
      });

      openDrawer();
      setDrawerWidth(drawerWidth);
    } else {
      navigate(
        `/rubialert/${isDraftsList ? "drafts" : "alerts"}/${item.id}/single`,
        {
          state: {
            // Where the single view sends the user back to, so the board keeps
            // its filters, search, sorting, view and loaded pages.
            from: `${location.pathname}${location.search}`,
            incidentStartDate,
            incidentEndDate,
            issuedStartDate,
            issuedEndDate,
            bulletinType,
            associatedCrime,
            policeServices,
            policeService,
            view,
            filtersVisible,
            index,
            searchText,
          },
        },
      );
    }
  };

  const scrollToTop = () => {
    topPageRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  useEffect(() => {
    if (similarTo !== null) {
      const startTime = Date.now();
      setIsLoadingModalOpen(true);
      const result = refetch();
      const endTime = Date.now();
      const timeElapsed = endTime - startTime;
      const remainingTime = Math.max(0, 8000 - timeElapsed);
      result.then(() => {
        setTimeout(() => {
          setIsLoadingModalOpen(false);
        }, remainingTime);
      });
    }
  }, [similarTo]);

  useEffect(() => {
    if (readOnly) {
      setIsReadOnlyDialogOpen(true);
    }
  }, [readOnly]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            fetchNextPage();
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );

    const currentAnchor = bottomPageRef.current;
    if (currentAnchor) {
      observer.observe(currentAnchor);
    }

    return () => {
      if (currentAnchor) {
        observer.unobserve(currentAnchor);
      }
    };
  }, [bottomPageRef.current]);

  useEffect(() => {
    if (updatedItems.length > 0) {
      refetch();
      setUpdatedItems([]);
    }
  }, [updatedItems]);

  useEffect(() => {
    if (myAlerts && currentUser?.id !== undefined) {
      refetch();
    }
    if (!currentUser?.id && user) {
      const setUser: ICreator = {
        email: user.email,
        first_name: user.first_name,
        last_name: user.last_name,
        badge_number: user.badge_number,
        id: user.id,
      };
      setCurrentUser(setUser as IUser);
    }
    if (!myAlerts) {
      refetch();
    }
  }, [myAlerts, currentUser?.id]);

  useEffect(() => {
    if (isDraftsList && readOnly) {
      return;
    }
    if (data) {
      const items = data.pages.flatMap((page) => page.items);
      setCurrentItems(items as unknown as BulletinResourceResponse[]);
      setTotalAlerts(data.pages[0].total_count);
    }
  }, [data]);

  useEffect(() => {
    if (isLoadingAlerts && hasNextPage) {
      loadMore();
    } else if (isLoadingAlerts && !hasNextPage) {
      setIsLoadingAlerts(false);
    }
  }, [isLoadingAlerts, hasNextPage]);

  useEffect(() => {
    if (bulletinId) {
      const alert = currentItems.find(
        (item) => item.id === parseInt(bulletinId),
      );
      if (alert) {
        setSearchParams((searchParams) => {
          searchParams.set("bulletinId", bulletinId);
          return searchParams;
        });
        openDrawer();
      }
      openDrawer();
    } else {
      closeDrawer();
    }
  }, [bulletinId]);

  if (alertsError) {
    toast.error("Error fetching alerts");
  }

  const renderDraftsView = () => {
    return (
      <AlertTable
        alertClickHandler={alertClickHandler}
        loadMore={loadMore}
        isDraftsList={isDraftsList}
        refetch={refetch}
      />
    );
  };

  const renderDisabledComponentDialog = () => {
    return (
      <DisabledComponentDialog
        isDialogOpen={isReadOnlyDialogOpen}
        handleClose={() => {
          navigate(`/rubialert/alerts`);
        }}
        title="You do not have access to create bulletins."
        message={
          <Typography>
            Please contact{" "}
            <Link
              rel="noopener noreferrer"
              target="_blank"
              href={mailTo({
                email: "support@tryrubicon.com",
                subject: "Requesting Access to Create Bulletins",
                body: `Hello Rubicon Support Team, 

                  I would like to request access to create bulletins.

                  Thank you. 

                  Sincerely,

                  ${currentUser?.first_name} ${currentUser?.last_name} 
                  ${currentUser?.email}`,
              })}
              color="secondary"
            >
              support@tryrubicon.com
            </Link>{" "}
            to request access.
          </Typography>
        }
      />
    );
  };

  const renderAlertsList = () => {
    return (
      <div className="alert-list">
        <div id="top-page-div" ref={topPageRef}></div>
        <div className="search-bar-container pl-0 mt-0 pl-md-4 mt-md-2">
          {!isDraftsList && (
            <>
              <div className="search-bar">
                <div
                  ref={
                    searchBarRef as unknown as {
                      current: HTMLDivElement | null;
                    }
                  }
                  style={{ maxWidth: "800px" }}
                  className="search-bar-container d-flex flex-row"
                >
                  <AlertSearchInput
                    searchText={searchText || ""}
                    loading={isLoadingAlerts}
                    onSearch={(text) => {
                      setSearchText(text);
                      setIsLoadingAlerts(true);
                      setCurrentItems([]);
                      scrollToTop();
                    }}
                    onClear={() => {
                      setSearchText("");
                      scrollToTop();
                    }}
                  />
                  <AlertFilterButton onClick={() => setFiltersVisible(true)} />
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginTop: "8px",
                  }}
                >
                  <RenderAlertFilterChips
                    searchParams={searchParams}
                    setSearchParams={setSearchParams}
                    getFilters={(searchParams, view) =>
                      alertFilterConfig(searchParams, view)
                    }
                  />
                </div>
                <AlertViewTabs
                  view={view}
                  defaultView="card"
                  activeViews={["card", "list", "map"]}
                  onChange={(value) => {
                    setSearchParams((searchParams) => {
                      searchParams.set("view", value);
                      return searchParams;
                    });
                  }}
                />
              </div>
              {filtersVisible && (
                <AlertFilters
                  isOpen={filtersVisible}
                  anchorEl={searchBarRef.current}
                  onClose={() => setFiltersVisible(false)}
                />
              )}
            </>
          )}
        </div>

        {!isLoading && currentItems?.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Typography variant="h6">
              No bulletins found. Please try adjusting your search criteria.
            </Typography>
          </div>
        )}
        {view === "card" && (
          <>
            <div className="alert-items">
              {currentItems?.length > 0 &&
                currentItems.map((bulletin, index) => (
                  <BulletinCard
                    key={index}
                    ref={setBulletinCardRef(bulletin.id)}
                    alertItem={bulletin}
                    index={index}
                    alertClickHandler={() => {
                      alertClickHandler(bulletin, index);
                    }}
                    selected={
                      (bulletinId !== null &&
                        bulletin.id === parseInt(bulletinId, 10)) ||
                      (Boolean(pinToFront) &&
                        compareBulletinId !== null &&
                        bulletin.id === parseInt(compareBulletinId, 10))
                    }
                    pinned={
                      Boolean(pinToFront) &&
                      bulletinId !== null &&
                      bulletin.id === parseInt(bulletinId, 10)
                    }
                  />
                ))}
            </div>
            <div ref={bottomPageRef} />
            {(isLoading || currentItems.length < totalAlerts) && (
              <div
                className="loading-more"
                style={{
                  padding: "16px 0",
                }}
              >
                <CircularProgress />
              </div>
            )}
          </>
        )}
        {view === "map" && (
          <>
            <GoogleMapsProvider>
              <AlertMap
                isLoading={isLoading}
                alerts={currentItems}
                alertClickHandler={alertClickHandler}
                selectedAlertId={bulletinId ? parseInt(bulletinId) : null}
              />
            </GoogleMapsProvider>
            {/* <div className={styles.loadMoreContainer}>
              <div className="alert-count">
                Loaded {currentItems.length} of {totalAlerts}
              </div>
              <Button
                variant="contained"
                color="primary"
                disabled={isFetchingNextPage || !hasNextPage}
                onClick={() => {
                  loadMore();
                }}
                endIcon={
                  isFetchingNextPage ? <CircularProgress size={20} /> : null
                }
              >
                {isFetchingNextPage ? "Loading..." : "Load More Bulletins"}
              </Button>
            </div> */}
          </>
        )}
        {view === "list" && (
          <AlertTable
            alertClickHandler={(item, index) => alertClickHandler(item, index)}
            loadMore={loadMore}
            isDraftsList={isDraftsList}
            refetch={refetch}
            selectedAlertId={bulletinId ? parseInt(bulletinId) : null}
          />
        )}
      </div>
    );
  };

  const renderLoadingModal = () => {
    const loadingMessages = [
      "Initializing the AI Engine...",
      "Using AI to Analyze all the bulletins...",
      "Computing the similarity matrix...",
      "Ranking the bulletins...",
    ];

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <LoadingModal
          stages={loadingMessages}
          timingMessage="Finding similar bulletins . . ."
        />
      </div>
    );
  };

  const renderAlertsView = () => {
    return (
      <SlideOutDrawer
        width={slideoutWidth}
        slideoutLocation="right"
        slideoutContent={
          bulletinId ? (
            <DisplayAlertDrawer mode={PUBLISHED} queryNextPage={loadMore} />
          ) : (
            <div></div>
          )
        }
        mainContent={
          isLoadingModalOpen ? renderLoadingModal() : renderAlertsList()
        }
        onDrawerClose={() => {
          // Dropping bulletinId is what closes the drawer — the effect above
          // syncs it. Closing it here too would leave the drawer shut with the
          // bulletin still in the URL if accessGuard blocks the navigation.
          setSearchParams((searchParams) => {
            searchParams.delete("bulletinId");
            searchParams.delete("pinToFront");
            searchParams.delete("compareBulletinId");
            return searchParams;
          });
        }}
      />
    );
  };

  if (isDraftsList && readOnly) {
    return renderDisabledComponentDialog();
  } else if (isDraftsList) {
    return renderDraftsView();
  } else {
    return renderAlertsView();
  }
};
